import React from 'react';

import {AnalysisFlowchartAction} from 'analysis-tool/flowchart/actions/AnalysisFlowchartActionTypes';
import {useAnalysisFlowchartContext} from 'analysis-tool/flowchart/AnalysisFlowchartContext';
import {STEP_TYPE_TO_USER_FACING_NAME} from 'graphql-client/__generated/GeneratedEnumMaps';
import notifications from 'notifications';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import isStepTypeEnabledForUser from 'pipelines/PipelineCapabilities';
import {getSourceStepTypes, getLatestStepVersion} from 'pipelines/StepConnectionUtils';
import {getStepTypeIconOrDefault} from 'pipelines/StepIconUtils';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';

export default function AddSourceStepButton(): JSX.Element | null {
  const config = useAnalysisFlowchartContext();
  const {enabled, doAction, message} = config.getActionInfo(AnalysisFlowchartAction.ADD_SOURCE_STEP);
  if (!enabled && !message) {
    return null;
  }

  // Only allow adding source steps, which can be configured in the generic way
  const sourceStepTypes = getSourceStepTypes()
    .filter((stepType) => stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType)))
    .filter((stepType) => isStepTypeEnabledForUser(stepType));

  if (sourceStepTypes.length === 0) {
    return null;
  }

  return (
    <Dropdown
      menu={
        <Menu>
          {sourceStepTypes.map((stepType) => (
            <Option
              key={STEP_TYPE_TO_USER_FACING_NAME[stepType]}
              icon={getStepTypeIconOrDefault(stepType, 'table')}
              isDisabled={!enabled}
              tooltipTitle={message}
              onSelect={() => {
                // NOTE: this should be impossible - select will be disabled if the action is not enabled
                if (!enabled) {
                  notifications.error('Add source step is not enabled');
                  return;
                }

                doAction(stepType);
              }}
            >
              {STEP_TYPE_TO_USER_FACING_NAME[stepType]}
            </Option>
          ))}
        </Menu>
      }
    >
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        isDropdown={true}
        text="Add source step"
      />
    </Dropdown>
  );
}
